import { app, nestedAppAuth } from "@microsoft/teams-js";
import {
  FluentProvider,
  teamsDarkTheme,
  teamsHighContrastTheme,
  teamsLightTheme,
  webLightTheme,
  type Theme,
} from "@fluentui/react-components";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { hasAccount, initAuth } from "../auth/msal";
import { SignIn } from "../components/SignIn";
import { DEMO } from "../config";

export interface HostInfo {
  /** true se l'app gira dentro Teams (o altro host Microsoft 365). */
  inTeams: boolean;
  context?: app.Context;
  /** Gruppo M365 del team corrente (per risalire al sito SharePoint). */
  groupId?: string;
  channelId?: string;
  /** true se aperta come scheda di canale (anche privato o condiviso). */
  inChannel: boolean;
  userPrincipalName?: string;
  locale: string;
  /** Host che ospita l'app: "Teams", "SharePoint", "Outlook"… (undefined nel browser). */
  hostName?: string;
  /** true se l'host fornisce il token (Nested App Authentication): niente schermata di accesso. */
  naa: boolean;
}

const HostContext = createContext<HostInfo | undefined>(undefined);

export function useHost(): HostInfo {
  const v = useContext(HostContext);
  if (!v) throw new Error("useHost() fuori da <TeamsProvider>");
  return v;
}

const themes: Record<string, Theme> = {
  default: teamsLightTheme,
  dark: teamsDarkTheme,
  contrast: teamsHighContrastTheme,
};

/** Inizializza Teams JS (con timeout, per poter girare anche nel browser) e l'autenticazione. */
async function bootstrap(): Promise<HostInfo & { needsSignIn: boolean }> {
  const framed = window.parent !== window.self;
  // ?embed=1: app incorporata in una pagina web (es. web part "Incorpora" di SharePoint).
  // Lì Teams non c'è: si salta l'attesa dell'inizializzazione (fino a 5 secondi).
  const embedded = new URLSearchParams(window.location.search).has("embed");
  let context: app.Context | undefined;

  if (framed && !embedded) {
    try {
      await withTimeout(app.initialize(), 5000);
      context = await app.getContext();
    } catch {
      context = undefined;
    }
  }

  const host: HostInfo = {
    inTeams: !!context,
    context,
    // Nei canali privati/condivisi il team "proprietario" è in channel.ownerGroupId
    groupId: context?.channel?.ownerGroupId ?? context?.team?.groupId,
    channelId: context?.channel?.id,
    inChannel: !!context?.channel?.id,
    userPrincipalName: context?.user?.userPrincipalName,
    locale: context?.app.locale ?? navigator.language,
    hostName: context?.app.host.name,
    naa: !!context && naaRecommended(),
  };

  // Accesso automatico solo se l'host fornisce davvero il token (Teams sì; SharePoint
  // o altri host forse no): altrimenti si mostra "Accedi con Microsoft", come nel browser.
  let needsSignIn = false;
  if (!DEMO) {
    await initAuth(context?.user?.loginHint, host.naa);
    needsSignIn = !(await hasAccount());
  }
  if (context) app.notifySuccess();
  return { ...host, needsSignIn };
}

export function TeamsProvider({ children }: { children: ReactNode }) {
  const [host, setHost] = useState<HostInfo>();
  const [needsSignIn, setNeedsSignIn] = useState(false);
  const [error, setError] = useState<string>();
  const [theme, setTheme] = useState<Theme>(webLightTheme);

  useEffect(() => {
    bootstrap()
      .then(({ needsSignIn: n, ...h }) => {
        setNeedsSignIn(n);
        setHost(h);
        if (h.context) {
          setTheme(themes[h.context.app.theme] ?? teamsLightTheme);
          app.registerOnThemeChangeHandler((t) => setTheme(themes[t] ?? teamsLightTheme));
        }
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  return (
    <FluentProvider theme={theme} style={{ minHeight: "100vh", background: "transparent" }}>
      {error ? (
        <div style={{ padding: 24 }}>Errore di avvio: {error}</div>
      ) : host && needsSignIn ? (
        <SignIn onDone={() => setNeedsSignIn(false)} />
      ) : host ? (
        <HostContext.Provider value={host}>{children}</HostContext.Provider>
      ) : null}
    </FluentProvider>
  );
}

function naaRecommended(): boolean {
  try {
    return nestedAppAuth.isNAAChannelRecommended();
  } catch {
    return false;
  }
}

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return Promise.race([p, new Promise<T>((_, reject) => setTimeout(() => reject(new Error("timeout")), ms))]);
}
